/**
 * Marker ordering as a multi-step comparator. Each step reports less/equal/
 * greater; a tie (equal) falls through to the next step. Lower result sorts
 * first = higher priority. Priority drives both marker z-index and the overlap
 * declutter (see marker-collision.ts).
 */
export type MarkerOrderInput = {
  id: string;
  /** Rule 1: the actively selected marker is prioritized. */
  selected: boolean;
  /** Rule 2: markers whose slots are all dashes ("no value") are de-prioritized. */
  hasData: boolean;
  /**
   * Rule 3: the unified dashboard + fallback priority (dashboard-listed first,
   * then fallback-listed, then unlisted), already resolved to a rank by
   * `orderStationsByPriority`. Lower rank = higher priority.
   */
  basePriority: number;
};

/**
 * Compare two markers for ordering. Steps, in order: (1) selected wins, (2) a
 * marker with data beats an all-dashes marker, (3) the unified base priority.
 * Keeping rule 2 above rule 3 means an all-dashes marker never outranks a marker
 * that has data, so it can never suppress it in the overlap declutter.
 */
export function compareMarkerOrder(a: MarkerOrderInput, b: MarkerOrderInput): number {
  if (a.selected !== b.selected) return a.selected ? -1 : 1;
  if (a.hasData !== b.hasData) return a.hasData ? -1 : 1;
  return a.basePriority - b.basePriority;
}

/**
 * Rank markers by {@link compareMarkerOrder}, returning each marker id mapped to
 * its position (0 = highest priority). The sort is stable, so equal markers keep
 * their input order.
 */
export function rankMarkersByOrder(inputs: MarkerOrderInput[]): Map<string, number> {
  const ordered = [...inputs].sort(compareMarkerOrder);
  const rankById = new Map<string, number>();
  ordered.forEach((input, rank) => rankById.set(input.id, rank));
  return rankById;
}
